import { reactive, ref } from 'vue';
import { parseApiResponse } from '../utils/api.js';

const createReferral = () => ({
  key: crypto.randomUUID(),
  full_name: '',
  phone: '',
});

export function createPublicPage({ message }) {
  return {
    setup() {
      const loading = ref(false);
      const successMessage = ref('');
      const formState = reactive({
        company: '',
        rendered_at: Math.floor(Date.now() / 1000),
        referrer: {
          full_name: '',
          phone: '',
        },
        referrals: [createReferral()],
      });

      const addReferral = () => {
        formState.referrals.push(createReferral());
      };

      const removeReferral = (key) => {
        if (formState.referrals.length === 1) {
          return;
        }

        const index = formState.referrals.findIndex((item) => item.key === key);
        if (index >= 0) {
          formState.referrals.splice(index, 1);
        }
      };

      const resetForm = () => {
        formState.company = '';
        formState.rendered_at = Math.floor(Date.now() / 1000);
        formState.referrer.full_name = '';
        formState.referrer.phone = '';
        formState.referrals = [createReferral()];
      };

      const submit = async () => {
        loading.value = true;
        successMessage.value = '';

        try {
          const response = await fetch('/api/referrals', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              company: formState.company,
              rendered_at: formState.rendered_at,
              source_channel: 'web',
              referrer: formState.referrer,
              referrals: formState.referrals.map(({ full_name, phone }) => ({ full_name, phone })),
            }),
          });

          const result = await parseApiResponse(response);

          if (result.__nonJson) {
            throw new Error(`Сервер вернул не JSON, а ${response.status} ${response.statusText}. Проверь /api/referrals и ответ сервера.`);
          }

          if (!response.ok) {
            const errors = result.errors ? Object.values(result.errors).join('\n') : result.message;
            throw new Error(errors);
          }

          successMessage.value = `Заявка #${result.data.submission_id} сохранена. Добавлено рефералов: ${result.data.referrals_count}.`;
          message.success(result.message);
          resetForm();
        } catch (error) {
          message.error(error.message || 'Не удалось отправить заявку.');
        } finally {
          loading.value = false;
        }
      };

      return {
        addReferral,
        formState,
        loading,
        removeReferral,
        submit,
        successMessage,
      };
    },
    template: `
      <div class="page-shell">
        <header class="brand-header">
          <a href="/" class="brand-mark brand-mark-link" aria-label="Bazis Motors">
            <img src="/images/logo_bazis_8.png" alt="БАЗИС МОТОРС" class="brand-logo" />
            <span class="brand-program-label">Реферальная программа</span>
          </a>
          <nav class="brand-nav">
            <a href="/" class="is-active">Форма регистрации</a>
            <a href="/conditions">Условия программы</a>
          </nav>
        </header>

        <section class="hero-card hero-card--bazis">
          <div class="hero-copy">
            <span class="eyebrow eyebrow--red">Agent BZS</span>
            <h1>Приведите клиента и получите агентское вознаграждение</h1>
            <p>
              Оставьте свои данные и передайте контакты потенциального покупателя.
              Заявки из сайта, Telegram и VK собираются в единой системе и обрабатываются менеджерами компании.
            </p>
            <div class="hero-links">
              <a href="/conditions" class="primary-link">Условия участия</a>
              <span class="hero-note">Вознаграждение выплачивается после подтверждения сделки.</span>
            </div>
          </div>
          <div class="hero-badge hero-badge--money">
            <strong>15&nbsp;000&nbsp;₽</strong>
            <span>за подтвержденную рекомендацию</span>
          </div>
        </section>

        <section class="brand-info-panel">
          <div class="brand-info-panel__copy">
            <h2>Что потребуется от вас</h2>
            <p>
              Заполните свои контакты, добавьте одного или нескольких потенциальных клиентов
              и отправьте заявку. Повторные обращения будут привязаны к вашей существующей записи.
            </p>
          </div>
          <div class="brand-info-panel__aside">
            <p><strong>Каналы программы</strong></p>
            <p>Сайт, Telegram и VK работают с одной базой заявок и общей историей рефералов.</p>
          </div>
        </section>

        <a-card class="brand-card form-card" :bordered="false">
          <template #title>Данные агента, это Вы, тот кто передает</template>

          <a-form layout="vertical" @submit.prevent="submit">
            <div class="honeypot-field" aria-hidden="true">
              <a-input v-model:value="formState.company" tabindex="-1" autocomplete="off" placeholder="Company" />
            </div>

            <div class="grid-two">
              <a-form-item label="ФИО">
                <a-input v-model:value="formState.referrer.full_name" size="large" placeholder="Иванов Иван Иванович" />
              </a-form-item>

              <a-form-item label="Телефон">
                <a-input v-model:value="formState.referrer.phone" size="large" placeholder="+7 900 000-00-00" />
              </a-form-item>
            </div>

            <div class="section-header">
              <div>
                <h2>Потенциальные покупатели, кого передаете, кому Вы порекомендовали нас.</h2>
                <p>Укажите клиентов, которых хотите передать менеджеру компании.</p>
              </div>
              <a-button type="default" size="large" @click="addReferral">Добавить клиента</a-button>
            </div>

            <div class="referral-list">
              <a-card
                v-for="(referral, index) in formState.referrals"
                :key="referral.key"
                class="subtle-card referral-card"
                size="small"
              >
                <template #title>Клиент {{ index + 1 }}</template>
                <template #extra>
                  <a-button type="text" danger @click="removeReferral(referral.key)">Удалить</a-button>
                </template>

                <div class="grid-two">
                  <a-form-item label="ФИО">
                    <a-input v-model:value="referral.full_name" size="large" placeholder="Петров Петр Петрович" />
                  </a-form-item>

                  <a-form-item label="Телефон">
                    <a-input v-model:value="referral.phone" size="large" placeholder="+7 900 000-00-00" />
                  </a-form-item>
                </div>
              </a-card>
            </div>

            <div class="actions">
              <a-button type="primary" size="large" :loading="loading" @click="submit">Отправить заявку</a-button>
              <a href="/conditions" class="inline-link">Смотреть условия программы</a>
            </div>

            <p v-if="successMessage" class="success-copy">{{ successMessage }}</p>
          </a-form>
        </a-card>
      </div>
    `,
  };
}
